import type {Event as SentryEvent, EventHint} from '@sentry/react';

/**
 * Describes a known-benign error that should not be reported to Sentry. A rule matches when
 * `name` equals the error's name OR `messagePattern` matches its message; at least one must be set.
 * @registry Shape of a rule describing a benign error to suppress from Sentry.
 */
export interface IgnoredErrorRule {
  /** Human-readable reason the error is ignored (documentation only). */
  reason: string;
  /** Exact match against the exception name / type. */
  name?: string;
  /** Pattern match against the exception message / value. */
  messagePattern?: RegExp;
}

/**
 * Known-benign errors already handled elsewhere in the app. Add a rule here to stop a
 * noisy error from polluting Sentry — no other code changes required.
 * @registry List of known-benign error rules to suppress from Sentry reporting.
 */
export const IGNORED_ERROR_RULES: IgnoredErrorRule[] = [
  {
    reason:
      'Amplify Cognito throws this when a perceived-but-invalid session still ' +
      'exists. Our sign-in flows clear the session and guard against it, so it ' +
      'is benign and non-actionable.',
    name: 'UserAlreadyAuthenticatedException',
    messagePattern: /there is already a signed in user/i,
  },
  {
    reason:
      'Firefox/Brave on iOS inject reader-mode content scripts into the page ' +
      'that reference window.__firefox__. When the namespace setup races or ' +
      'fails, the injected script throws in global code. Browser-injected, ' +
      'not app code, non-actionable.',
    messagePattern: /__firefox__/,
  },
  {
    reason:
      'Crypto-wallet browser extensions (Brave Wallet, MetaMask, etc.) inject ' +
      'window.ethereum and throw when their own injection races. ' +
      'Browser-injected, not app code, non-actionable.',
    messagePattern: /window\.ethereum/,
  },
  {
    reason:
      'Other wallet extensions inject window.solana / window.web3, and two ' +
      'wallets fighting over the same global throw "Cannot redefine ' +
      'property". Browser-injected, not app code, non-actionable.',
    messagePattern:
      /window\.(solana|web3)|cannot (redefine|set) property:? ?(ethereum|solana|web3)/i,
  },
  {
    reason:
      'Benign browser signal that layout was recalculated across observer ' +
      'callbacks; per the spec it is informational and never breaks anything. ' +
      'Fires constantly from size-observing UI (Mantine, data grids).',
    messagePattern:
      /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/,
  },
  {
    reason:
      'In-app WebViews (Instagram, Facebook, Slack, etc.) inject scripts that ' +
      'reference window.webkit.messageHandlers and throw when their own setup ' +
      'races. Browser-injected, not app code, non-actionable.',
    messagePattern: /webkit\.messageHandlers/,
  },
  {
    reason:
      'Google Translate rewrites the DOM when a user translates the page, ' +
      'producing errors that reference its own internals. Not fixable in app ' +
      'code.',
    messagePattern: /translate_http|a\[b\]\.target\.className\.indexOf/,
  },
];

/**
 * Returns true when a Sentry event matches one of the {@link IGNORED_ERROR_RULES}
 * and should be dropped. Intended to back Sentry's `beforeSend` hook.
 * @registry Whether a Sentry event matches a known-benign ignore rule.
 */
export const shouldIgnoreSentryEvent = (
  event: SentryEvent,
  hint: EventHint
): boolean => {
  const error = hint?.originalException as
    | {name?: string; message?: string}
    | undefined;

  const name = error?.name ?? event.exception?.values?.[0]?.type;
  const message = error?.message ?? event.exception?.values?.[0]?.value ?? '';

  return IGNORED_ERROR_RULES.some(
    rule =>
      (rule.name !== undefined && rule.name === name) ||
      (rule.messagePattern !== undefined && rule.messagePattern.test(message))
  );
};
